// src/components/TaskList.jsx
import React, { useEffect, useState } from "react";
import api from "../api";

const STATUS_MAP = {
  todo: "Todo",
  in_progress: "In Progress",
  review: "Review",
  done: "Done",
};

export default function TaskList() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("todo");
  const [priority, setPriority] = useState("medium");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    loadTasks();
    loadProjects();
    loadEmployees();
  }, []);

  const loadTasks = async () => {
    const res = await api.get("/tasks");
    setTasks(res.data);
  };

  const loadProjects = async () => {
    const res = await api.get("/project");
    setProjects(res.data);
    if (res.data.length) setProjectId(res.data[0].id);
  };

  const loadEmployees = async () => {
    const res = await api.get("/employees");
    setEmployees(res.data);
    if (res.data.length) setAssigneeId(res.data[0].id);
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !projectId || !assigneeId) {
      setError("Required fields missing");
      return;
    }

    const payload = {
      project_id: projectId,
      title,
      description,
      status,
      priority,
      assignee_id: assigneeId,
      due_date: dueDate || null,
    };

    setLoading(true);
    if (editingId) {
      await api.put(`/tasks/${editingId}`, payload);
    } else {
      await api.post("/tasks", payload);
    }

    resetForm();
    loadTasks();
    setLoading(false);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setStatus("todo");
    setPriority("medium");
    setDueDate("");
    setEditingId(null);
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setProjectId(t.project_id);
    setTitle(t.title);
    setDescription(t.description || "");
    setStatus(t.status);
    setPriority(t.priority);
    setAssigneeId(t.assignee_id);
    setDueDate(t.due_date ? t.due_date.split("T")[0] : "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete task?")) return;
    await api.delete(`/tasks/${id}`);
    loadTasks();
  };

  /* ================= RENDER TABLE ================= */

  const renderTable = (statusKey, color) => {
    const filtered = tasks.filter((t) => t.status === statusKey);

    return (
      <div className="bg-white shadow rounded p-3">
        <h3 className={`text-lg font-semibold mb-2 ${color}`}>
          {STATUS_MAP[statusKey]} ({filtered.length})
        </h3>

        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th>ID</th>
              <th>Task</th>
              <th>Project</th>
              <th>Priority</th>
              <th>Assignee</th>
              <th>Due</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-t text-center">
                <td>{t.id}</td>
                <td>{t.title}</td>
                <td>{t.project_name}</td>
                <td className="capitalize">{t.priority}</td>
                <td>{t.assignee_name}</td>
                <td>{t.due_date?.split("T")[0] || "-"}</td>
                <td className="flex gap-1 justify-center">
                  <button
                    onClick={() => handleEdit(t)}
                    className="bg-yellow-400 px-2 text-white rounded"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="bg-red-500 px-2 text-white rounded"
                  >
                    Del
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="7" className="text-gray-400 py-2">
                  No tasks
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  /* ================= UI ================= */

  return (
    <div className="max-w-7xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4 text-center">Task Management</h2>

      {/* FORM */}
      <form onSubmit={handleSubmit} className="bg-white p-4 shadow rounded mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="border p-1 rounded">
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task Title"
            className="border p-1 rounded md:col-span-2" />

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border p-1 rounded">
            <option value="todo">Todo</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="done">Done</option>
          </select>

          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="border p-1 rounded">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>

          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className="border p-1 rounded">
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>

          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="border p-1 rounded" />

          <button className="bg-blue-500 text-white rounded px-3">
            {editingId ? "Update" : "Add"}
          </button>
        </div>
      </form>

      {/* STATUS TABLES */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderTable("todo", "text-gray-700")}
        {renderTable("in_progress", "text-blue-600")}
        {renderTable("review", "text-yellow-600")}
        {renderTable("done", "text-green-600")}
      </div>
    </div>
  );
}
